// SPDX-License-Identifier: AGPL-3.0-or-later

import * as ReminderCommands from '@app/features/messaging/commands/ReminderCommands';
import {ReminderDateModal} from '@app/features/messaging/components/modals/ReminderDateModal';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import Reminders from '@app/features/messaging/state/Reminders';
import {
	CLEAR_REMINDER_DESCRIPTOR,
	REMINDER_CHOOSE_A_DATE_DESCRIPTOR,
	REMINDER_PRESET_IDS,
	REMINDER_PRESET_LABELS,
	resolveReminderPreset,
} from '@app/features/messaging/utils/ReminderTimePresets';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import type {MenuItemType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import type {I18n} from '@lingui/core';

export const REMIND_CLEAR_ITEM_ID = 'remind_clear';

export function getRemindPresetMenuItems(
	message: Message,
	i18n: I18n,
	options?: {onSelect?: () => void},
): Array<MenuItemType> {
	const hasReminder = Reminders.hasPendingReminder(message.id);
	const presetItems = REMINDER_PRESET_IDS.map((id) => ({
		id: `remind_${id}`,
		label: i18n._(REMINDER_PRESET_LABELS[id]),
		onClick: () => {
			options?.onSelect?.();
			void ReminderCommands.create(i18n, {
				channelId: message.channelId,
				messageId: message.id,
				remindAt: resolveReminderPreset(id),
			});
		},
	}));
	const clearItem = hasReminder
		? [
				{
					id: REMIND_CLEAR_ITEM_ID,
					label: i18n._(CLEAR_REMINDER_DESCRIPTOR),
					danger: true,
					onClick: () => {
						options?.onSelect?.();
						void ReminderCommands.clear(i18n, {
							channelId: message.channelId,
							messageId: message.id,
						});
					},
				},
			]
		: [];
	return [
		...clearItem,
		...presetItems,
		{
			id: 'remind_choose_date',
			label: i18n._(REMINDER_CHOOSE_A_DATE_DESCRIPTOR),
			onClick: () => {
				ModalCommands.pushAfterBottomSheetClose(
					() => options?.onSelect?.(),
					modal(() => (
						<ReminderDateModal
							onSubmit={async (remindAt) => {
								await ReminderCommands.create(i18n, {
									channelId: message.channelId,
									messageId: message.id,
									remindAt,
								});
							}}
							data-flx="messaging.reminder-menu-utils.on-click.reminder-date-modal"
						/>
					)),
				);
			},
		},
	];
}
